/**
 * MessageAlert — renders Dexie Cloud message-alert interactions.
 *
 * Shown for: INVALID_OTP, INVALID_EMAIL, LICENSE_LIMIT_REACHED, GENERIC_ERROR, etc.
 *
 * For LICENSE_LIMIT_REACHED: onCancel may be null/undefined, meaning
 * the user cannot dismiss — they must contact support or upgrade.
 *
 * NEVER-17: This component must NEVER trigger data deletion.
 */

import { type DXCUserInteraction, resolveText } from "dexie-cloud-addon"
import { useTranslation } from "react-i18next"
import { Button } from "@/app/components/ui/button"
import { CircularProgress } from "@/app/components/ui/CircularProgress"
import { cn } from "@/shared/utils/general/cn"

interface MessageAlertProps {
  alerts: DXCUserInteraction["alerts"]
  onSubmit: DXCUserInteraction["onSubmit"]
  onCancel?: DXCUserInteraction["onCancel"]
  submitLabel?: string
  cancelLabel?: string | null
}

export function MessageAlert({
  alerts,
  onSubmit,
  onCancel,
  submitLabel,
  cancelLabel,
}: MessageAlertProps) {
  const { t } = useTranslation()

  // Determine severity for styling
  const hasError = alerts.some((a) => a.type === "error")
  const isLicenseLimit = alerts.some(
    (a) => a.messageCode === "LICENSE_LIMIT_REACHED",
  )

  // Detect connection errors — replace developer-facing messages with user-friendly ones
  const isConnectionError = alerts.some((a) => {
    const text = resolveText(a)?.toLowerCase() ?? ""
    return (
      text.includes("could not connect") ||
      text.includes("failed to fetch") ||
      text.includes("networkerror") ||
      text.includes("network error") ||
      text.includes("cors") ||
      text.includes("whitelist") ||
      text.includes("err_failed") ||
      text.includes("timeout")
    )
  })

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-lg">
        {/* Icon */}
        <div className="mb-4 flex justify-center">
          <div
            className={cn(
              "flex h-12 w-12 items-center justify-center rounded-full",
              isConnectionError
                ? "bg-[var(--yellow-500)]/10"
                : hasError
                  ? "bg-[var(--red-500)]/10"
                  : "bg-primary/10",
            )}
          >
            {isConnectionError ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--yellow-500)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M1 1l22 22" />
                <path d="M16.72 11.06A10.94 10.94 0 0112 10c-1.68 0-3.26.38-4.72 1.06" />
                <path d="M5 12.55a11 11 0 0114 0" />
                <path d="M8.53 16.11a6 6 0 016.95 0" />
                <line x1="12" y1="20" x2="12.01" y2="20" />
              </svg>
            ) : hasError ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--red-500)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            ) : (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-primary"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            )}
          </div>
        </div>

        {/* Alerts — connection errors get user-friendly messages */}
        {isConnectionError ? (
          <div className="mb-4 rounded-lg bg-[var(--yellow-500)]/10 px-4 py-3 text-sm text-center text-[var(--yellow-500)]">
            {t("Unable to connect to our servers. This could be a temporary issue — please try again in a few moments.")}
          </div>
        ) : (
          alerts.map((alert, i) => (
            <div
              key={i}
              className={cn(
                "mb-4 rounded-lg px-4 py-3 text-sm text-center",
                alert.type === "error" &&
                  "bg-[var(--red-500)]/10 text-[var(--red-500)]",
                alert.type === "warning" &&
                  "bg-[var(--yellow-500)]/10 text-[var(--yellow-500)]",
                alert.type === "info" && "bg-primary/10 text-primary",
              )}
            >
              {resolveText(alert)}
            </div>
          ))
        )}

        {/* License limit extra info */}
        {isLicenseLimit && (
          <p className="mb-4 text-center text-sm text-muted-foreground">
            {t(
              "Your workspace has reached the maximum number of users for your current plan.",
            )}
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {onSubmit ? (
            <Button
              type="button"
              variant="primary"
              onClick={() => onSubmit({})}
              className="h-12 w-full text-base"
            >
              {isConnectionError ? t("Try again") : (submitLabel ?? t("OK"))}
            </Button>
          ) : (
            /* onSubmit === undefined = Dexie Cloud still loading callback */
            <div className="flex justify-center py-3">
              <CircularProgress className="size-6" />
            </div>
          )}

          {onCancel && cancelLabel !== null && (
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              className="h-10 w-full text-sm"
            >
              {cancelLabel ?? t("Cancel")}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
